import DOMPurify from 'dompurify';

// Configuration for DOMPurify to allow rich content but prevent XSS
const purifyConfig = {
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'a', 'ul', 'ol', 'li',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'code', 'pre',
    'img', 'div', 'span'
  ],
  ALLOWED_ATTR: [
    'href', 'target', 'rel', 'src', 'alt', 'title', 'class'
  ],
  ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|data):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
  ADD_ATTR: ['target', 'rel'],
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'style'],
};

/**
 * Sanitizes HTML content to prevent XSS attacks while preserving rich text formatting
 * @param html - The HTML content to sanitize
 * @returns Sanitized HTML string
 */
export const sanitizeContent = (html: string): string => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  // Use DOMPurify to clean the HTML
  const cleanHTML = DOMPurify.sanitize(html, purifyConfig);

  // Additional custom sanitization
  return cleanHTML
    // Remove any remaining script tags that might have slipped through
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    // Remove any on* event handlers
    .replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '')
    // Ensure all links open in new tab and have security attributes
    .replace(/<a\s+([^>]*href\s*=\s*["'][^"']*["'][^>]*)>/gi, (match, attrs) => {
      if (!attrs.includes('target=')) {
        attrs += ' target="_blank"';
      }
      if (!attrs.includes('rel=')) {
        attrs += ' rel="noopener noreferrer"';
      }
      return `<a ${attrs}>`;
    });
};

/**
 * Strips all HTML tags and returns plain text
 * @param html - The HTML content to convert to plain text
 * @returns Plain text string
 */
export const stripHTML = (html: string): string => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  return DOMPurify.sanitize(html, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
};

/**
 * Extracts a preview text from rich content (useful for excerpts)
 * @param html - The HTML content
 * @param maxLength - Maximum length of the preview
 * @returns Preview text
 */
export const getContentPreview = (html: string, maxLength: number = 150): string => {
  const plainText = stripHTML(html);
  if (plainText.length <= maxLength) {
    return plainText;
  }
  
  return plainText.substring(0, maxLength).trim() + '...';
};

/**
 * Validates if the content contains any potentially unsafe elements
 * @param html - The HTML content to validate
 * @returns boolean indicating if content is safe
 */
export const isContentSafe = (html: string): boolean => {
  if (!html || typeof html !== 'string') {
    return true;
  }

  const sanitized = sanitizeContent(html);
  // If the sanitized content is significantly different from the original,
  // it likely contained unsafe elements
  return Math.abs(html.length - sanitized.length) < (html.length * 0.1);
};

/**
 * Extracts all image URLs from HTML content
 * @param html - The HTML content
 * @returns Array of image URLs
 */
export const extractImageUrls = (html: string): string[] => {
  if (!html || typeof html !== 'string') {
    return [];
  }

  const imgRegex = /<img[^>]+src\s*=\s*["']([^"']+)["'][^>]*>/gi;
  const urls: string[] = [];
  let match;

  while ((match = imgRegex.exec(html)) !== null) {
    urls.push(match[1]);
  }

  return urls;
};

/**
 * Extracts all links from HTML content
 * @param html - The HTML content
 * @returns Array of link objects with href and text
 */
export const extractLinks = (html: string): Array<{ href: string; text: string }> => {
  if (!html || typeof html !== 'string') {
    return [];
  }

  const linkRegex = /<a[^>]+href\s*=\s*["']([^"']+)["'][^>]*>([^<]*)<\/a>/gi;
  const links: Array<{ href: string; text: string }> = [];
  let match;

  while ((match = linkRegex.exec(html)) !== null) {
    links.push({
      href: match[1],
      text: match[2] || match[1]
    });
  }

  return links;
};
